import { useState, useEffect, useMemo } from 'react';
import { Button, Card, Badge, Spinner, Table, Form, InputGroup } from 'react-bootstrap';
import { BookOpen, Plus, Eye, Edit2, Trash2, Search } from 'lucide-react';
import { useAgentRegistry } from '../../../apps/hooks/agent/useAgentRegistry';
import type { ManualResponse, RegistryTreeResponse } from '../../../infrastructure/AIAgentRegistryClient';
import { ManualDetailModal } from './ManualDetailModal';
import { UpsertManualModal } from './UpsertManualModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { formatDate } from '../../../utils/dateUtils';

interface ManualTabProps {
  registryTree: RegistryTreeResponse | undefined;
}

export const ManualTab = ({ registryTree }: ManualTabProps) => {
  const { useManualsQuery, upsertManualMutation, deleteManualMutation } = useAgentRegistry();
  const [selectedCategory, setSelectedCategory] = useState('');

  const categories = useMemo(() => {
    return registryTree?.manuals.filter((m) => m.parentId === null) || [];
  }, [registryTree]);

  useEffect(() => {
    if (categories.length > 0 && !selectedCategory && categories[0]) {
      setSelectedCategory(categories[0].title.toLowerCase());
    }
  }, [categories, selectedCategory]);
  const [selectedManual, setSelectedManual] = useState<ManualResponse | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showUpsertModal, setShowUpsertModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingManual, setEditingManual] = useState<ManualResponse | null>(null);

  const manualsQuery = useManualsQuery(selectedCategory);
  const { data: manuals = [], isLoading } = manualsQuery;

  const handleViewManual = (manual: ManualResponse) => {
    setSelectedManual(manual);
    setShowDetailModal(true);
  };

  const handleCreateManual = () => {
    setEditingManual(null);
    setShowUpsertModal(true);
  };

  const handleEditManual = (manual: ManualResponse) => {
    setEditingManual(manual);
    setShowUpsertModal(true);
  };

  const handleDeleteManual = (manual: ManualResponse) => {
    setSelectedManual(manual);
    setShowDeleteModal(true);
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">
          <BookOpen className="me-2" size={20} />
          Manuals
        </h5>
        <Button variant="primary" size="sm" onClick={handleCreateManual}>
          <Plus size={16} className="me-1" /> New Manual
        </Button>
      </div>

      <div className="mb-4">
        <InputGroup>
          <InputGroup.Text><Search size={16} /></InputGroup.Text>
          <Form.Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.title.toLowerCase()}>{cat.title}</option>
            ))}
          </Form.Select>
        </InputGroup>
      </div>

      {isLoading ? (
        <div className="text-center my-5"><Spinner animation="border" /></div>
      ) : manuals.length === 0 ? (
        <Card body className="text-center text-muted">
          No manuals found in "{selectedCategory}" category.
        </Card>
      ) : (
        <Table hover responsive className="align-middle">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Category</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {manuals.map((manual) => (
              <tr key={manual.id} onClick={() => handleViewManual(manual)} style={{ cursor: 'pointer' }}>
                <td><strong>{manual.name}</strong></td>
                <td className="text-truncate" style={{ maxWidth: '300px' }}>{manual.description}</td>
                <td><Badge bg="secondary">{manual.category}</Badge></td>
                <td>{formatDate(manual.updatedAt)}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <Button variant="outline-info" size="sm" className="me-1" onClick={() => handleViewManual(manual)}><Eye size={14} /></Button>
                  <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => handleEditManual(manual)}><Edit2 size={14} /></Button>
                  <Button variant="outline-danger" size="sm" onClick={() => handleDeleteManual(manual)}><Trash2 size={14} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <ManualDetailModal
        show={showDetailModal}
        manual={selectedManual}
        onHide={() => setShowDetailModal(false)}
        onEdit={() => {
          setShowDetailModal(false);
          if (selectedManual) handleEditManual(selectedManual);
        }}
      />

      <UpsertManualModal
        show={showUpsertModal}
        manual={editingManual}
        defaultCategory={selectedCategory}
        registryTree={registryTree}
        onHide={() => setShowUpsertModal(false)}
        onSubmit={(data) => {
          upsertManualMutation.mutate(data);
          setShowUpsertModal(false);
        }}
        isSubmitting={upsertManualMutation.isPending}
      />

      <DeleteConfirmModal
        show={showDeleteModal}
        title="Delete Manual"
        message={`Are you sure you want to delete "${selectedManual?.name}"? This action cannot be undone.`}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={() => {
          if (selectedManual) deleteManualMutation.mutate(selectedManual.id);
          setShowDeleteModal(false);
        }}
        isDeleting={deleteManualMutation.isPending}
      />
    </>
  );
};

